import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { PageHeader } from '@/components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import {
  Users,
  ImageIcon,
  CreditCard,
  Loader2,
  Eye,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';

interface UserEntry {
  user: {
    id: string;
    email: string;
    name: string | null;
    credits: number;
    freeGenerationsUsed: number;
    role: string;
    createdAt: string;
  };
  generationsCount: number;
}

interface GenerationImage {
  id: string;
  storagePath: string;
  imageUrl: string;
  imageType: string;
}

interface GenerationEntry {
  id: string;
  productName: string;
  style: string;
  status: string;
  createdAt: string;
  originalImageUrl: string | null;
  images: GenerationImage[];
}

interface UserGenerationsData {
  user: UserEntry['user'];
  generations: GenerationEntry[];
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<UserEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal: gerações do usuário
  const [generationsModal, setGenerationsModal] = useState<{
    open: boolean;
    userId: string;
    userEmail: string;
  }>({ open: false, userId: '', userEmail: '' });
  const [generationsData, setGenerationsData] = useState<UserGenerationsData | null>(null);
  const [generationsLoading, setGenerationsLoading] = useState(false);

  // Modal: adicionar créditos
  const [creditsModal, setCreditsModal] = useState<{
    open: boolean;
    userId: string;
    userEmail: string;
    currentCredits: number;
  }>({ open: false, userId: '', userEmail: '', currentCredits: 0 });
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await api.get<UserEntry[]>('/api/admin/users');
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGenerationsModal = async (userId: string, userEmail: string) => {
    setGenerationsModal({ open: true, userId, userEmail });
    setGenerationsData(null);
    setGenerationsLoading(true);
    try {
      const data = await api.get<UserGenerationsData>(`/api/admin/users/${userId}/generations`);
      setGenerationsData(data);
    } catch (error) {
      console.error('Failed to load generations:', error);
    } finally {
      setGenerationsLoading(false);
    }
  };

  const closeGenerationsModal = () => {
    setGenerationsModal({ open: false, userId: '', userEmail: '' });
    setGenerationsData(null);
  };

  const openCreditsModal = (userId: string, userEmail: string, currentCredits: number) => {
    setCreditsModal({ open: true, userId, userEmail, currentCredits });
    setCreditsAmount('');
    setCreditsReason('');
    setCreditsError('');
  };

  const closeCreditsModal = () => {
    setCreditsModal({ open: false, userId: '', userEmail: '', currentCredits: 0 });
  };

  const handleAddCredits = async () => {
    const amount = parseInt(creditsAmount, 10);
    if (!creditsAmount || isNaN(amount) || amount <= 0) {
      setCreditsError('Informe uma quantidade válida maior que zero.');
      return;
    }
    setCreditsLoading(true);
    setCreditsError('');
    try {
      await api.post(`/api/admin/users/${creditsModal.userId}/credits`, {
        amount,
        reason: creditsReason || undefined,
      });
      closeCreditsModal();
      loadUsers();
    } catch (error) {
      setCreditsError('Erro ao adicionar créditos. Tente novamente.');
      console.error('Failed to add credits:', error);
    } finally {
      setCreditsLoading(false);
    }
  };

  const statusIcon = (status: string) => {
    if (status === 'completed') return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
    if (status === 'failed') return <XCircle className="h-3.5 w-3.5 text-red-500" />;
    return <Clock className="h-3.5 w-3.5 text-yellow-500" />;
  };

  const totalUsers = users.length;
  const totalCredits = users.reduce((sum, u) => sum + u.user.credits, 0);
  const totalGenerations = users.reduce((sum, u) => sum + u.generationsCount, 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Usuários"
          subtitle="Veja as imagens geradas pelos usuários e gerencie créditos"
        />

        {/* Stats */}
        <div className="grid gap-4 grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <p className="text-sm text-gray-500">Usuários</p>
              <p className="text-3xl font-bold">{totalUsers}</p>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <p className="text-sm text-gray-500">Gerações totais</p>
              <p className="text-3xl font-bold">{totalGenerations}</p>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <p className="text-sm text-gray-500">Créditos em circulação</p>
              <p className="text-3xl font-bold">{totalCredits}</p>
            </CardHeader>
          </Card>
        </div>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>Todos os Usuários</CardTitle>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum usuário cadastrado ainda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((entry) => (
                  <div
                    key={entry.user.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border rounded-lg"
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{entry.user.email}</span>
                        {entry.user.role === 'admin' && (
                          <Badge variant="default" className="text-xs">Admin</Badge>
                        )}
                      </div>
                      {entry.user.name && (
                        <p className="text-xs text-gray-500">{entry.user.name}</p>
                      )}
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1 text-xs text-gray-600">
                          <ImageIcon className="h-3.5 w-3.5" />
                          {entry.generationsCount} {entry.generationsCount === 1 ? 'geração' : 'gerações'}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-600">
                          <CreditCard className="h-3.5 w-3.5" />
                          {entry.user.credits} crédito{entry.user.credits !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(entry.user.createdAt).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openGenerationsModal(entry.user.id, entry.user.email)}
                        className="gap-1.5 text-xs"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Ver gerações
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          openCreditsModal(entry.user.id, entry.user.email, entry.user.credits)
                        }
                        className="gap-1.5 text-xs text-green-700 border-green-300 hover:bg-green-50"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        Créditos
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal: Gerações do usuário */}
      <Dialog open={generationsModal.open} onOpenChange={(open) => !open && closeGenerationsModal()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gerações de {generationsModal.userEmail}</DialogTitle>
          </DialogHeader>

          {generationsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
            </div>
          ) : !generationsData || generationsData.generations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>Nenhuma geração encontrada</p>
            </div>
          ) : (
            <div className="space-y-6">
              {generationsData.generations.map((gen) => (
                <div key={gen.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <p className="font-medium">{gen.productName}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(gen.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {statusIcon(gen.status)}
                      <Badge variant="secondary" className="text-xs capitalize">
                        {gen.status}
                      </Badge>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    {/* Input image */}
                    {gen.originalImageUrl && (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-400 font-medium">Input</p>
                        <a
                          href={gen.originalImageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block aspect-square rounded overflow-hidden border-2 border-dashed border-gray-300 hover:opacity-90 transition-opacity"
                        >
                          <img
                            src={gen.originalImageUrl}
                            alt="Input"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </a>
                      </div>
                    )}

                    {/* Generated images */}
                    {gen.images.length === 0 ? (
                      <p className="text-xs text-gray-400 col-span-3 self-center">Sem imagens geradas</p>
                    ) : (
                      gen.images.map((img, idx) => (
                        <div key={img.id} className="space-y-1">
                          <p className="text-xs text-gray-400 font-medium">Gerada {idx + 1}</p>
                          <a
                            href={img.imageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block aspect-square rounded overflow-hidden border hover:opacity-90 transition-opacity"
                          >
                            <img
                              src={img.imageUrl}
                              alt={gen.productName}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Adicionar créditos */}
      <Dialog open={creditsModal.open} onOpenChange={(open) => !open && closeCreditsModal()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Adicionar Créditos</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Usuário: <span className="font-medium">{creditsModal.userEmail}</span>
            </p>
            <p className="text-sm text-gray-600">
              Créditos atuais: <span className="font-medium">{creditsModal.currentCredits}</span>
            </p>

            <div className="space-y-2">
              <Label htmlFor="credits-amount">Quantidade de créditos *</Label>
              <Input
                id="credits-amount"
                type="number"
                min="1"
                placeholder="Ex: 5"
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="credits-reason">Motivo (opcional)</Label>
              <Input
                id="credits-reason"
                type="text"
                placeholder="Ex: Teste gratuito, Compensação, etc."
                value={creditsReason}
                onChange={(e) => setCreditsReason(e.target.value)}
              />
            </div>

            {creditsError && (
              <p className="text-sm text-red-600">{creditsError}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeCreditsModal} disabled={creditsLoading}>
              Cancelar
            </Button>
            <Button onClick={handleAddCredits} disabled={creditsLoading}>
              {creditsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Adicionar Créditos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
